import { notification } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import DocumentUploadList from './modals/DocumentUploadList';
import ConfirmScanModal from './modals/ConfirmScanModal';
import React from 'react';
import { detectTiffFile } from '../../shared/utils';

const DocumentUploader = ({
  onFileUploaded,
  wo_files,
  overrideUploadProps = {},
  customClass = '',
  showTitle = true,
  maxVisibleItems = null,
  childRef,
  onClickFile,
  forScan = [],
  showScanModal = true,
}) => {
  const [fileList, setFileList] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pending, setPending] = useState([]);

  useEffect(() => {
    if (wo_files.wo_documents && wo_files.wo_documents.length > 0) {
      const getFileName = (documentOrUrl, index) => {
        // If documentOrUrl is an object with a name property, return that
        if (documentOrUrl && documentOrUrl.name) {
          return documentOrUrl.name;
        }

        // Ensure documentOrUrl is a string before using split
        if (typeof documentOrUrl !== 'string') {
          // If it's an object without a name but with a url, use that
          if (documentOrUrl && documentOrUrl.url) {
            return getFileName(documentOrUrl.url, index);
          }
          // If we can't determine a name, use a default
          return `Document ${index + 1}`;
        }

        // Process string URLs
        // here is a file name example: dcff6c40-9013-11ef-b0d4-9d8c8c0161cdCanvec F225283 (1).pdf
        // check pattern of uuid.v1
        const uuidPattern = new RegExp(
          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/
        );
        const name = documentOrUrl.split('/').pop(); // get the last part of the URL
        if (uuidPattern.test(name)) {
          return name.replace(uuidPattern, ''); // remove the UUID
        }
        return name;
      };

      const initialFileList = wo_files.wo_documents.map(
        (documentOrUrl, index) => {
          // Determine the correct URL value
          let url;
          if (documentOrUrl && typeof documentOrUrl === 'object') {
            url = documentOrUrl.url || '';
          } else if (typeof documentOrUrl === 'string') {
            url = documentOrUrl;
          } else {
            url = '';
          }

          // Ensure url is a string
          if (typeof url !== 'string') {
            url = String(url || '');
          }

          return {
            uid: `old-doc-${index}`,
            name: getFileName(documentOrUrl, index),
            status: 'done',
            url: url,
          };
        }
      );

      setFileList(initialFileList);
    }
  }, [wo_files.wo_documents]);

  const giveFilesMeta = (files) => {
    return files.map((file) => ({
      uid: file.uid,
      name: file.name,
      status: 'done',
      url: URL.createObjectURL(file),
      originFileObj: file,
      type: file.type,
      size: file.size,
    }));
  };

  const handleFileChange = ({ file, fileList: newFileList }) => {
    const filteredList = newFileList.filter(
      (f) => !f.size || f.size / 1024 / 1024 <= 12
    );

    if (newFileList.length > filteredList.length) {
      notification.open({
        message: <div style={{ color: 'red' }}>Error</div>,
        description: 'File size must be less than 12MB.',
      });
    }
    setFileList(filteredList);
  };

  const beforeUpload = async (file) => {
    // Check if file is actually a TIFF (even if extension is wrong)
    const isTiff = await detectTiffFile(file);
    if (isTiff) {
      notification.error({
        message: 'TIFF files not allowed',
        description: 'TIFF files are not supported. Uploade from SFTP instead.',
      });
      return false;
    }

    // Check file size
    const isGreaterThan10M = file.size / 1024 / 1024 > 12;
    if (isGreaterThan10M) {
      notification.error({
        message: 'File too large',
        description: 'File size must be less than 12MB.',
      });
      return false;
    }

    if (!showScanModal) {
      setFileList((curr) => [...curr, file]);
      onFileUploaded(file);
      return false;
    } else {
      setPending((prev) => [...prev, file]);
      if (!showConfirmationModal) setShowConfirmationModal(true);
      return false;
    }
  };

  const handleConfirmScan = (scanFiles) => {
    if (pending) {
      const noScan = pending.filter(
        (pendingFile) => !scanFiles.includes(pendingFile)
      );
      const filesWithMeta = giveFilesMeta(noScan);
      const scanFilesWithMeta = giveFilesMeta(scanFiles);
      setFileList((curr) => [...curr, ...filesWithMeta]);
      filesWithMeta.forEach((file) => onFileUploaded(file));
      setPending([]);
      forScan(scanFilesWithMeta);
      setShowConfirmationModal(false);
    }
  };

  if (childRef) {
    childRef({
      beforeUpload,
      setFileList,
      getFileList: () => fileList,
    });
  }

  // Custom item render function to add download link
  const itemRender = (originNode, file, fileList, actions) => {
    return React.cloneElement(originNode, {
      children: (
        <>
          {originNode.props.children}
          {file.url && (
            <a
              href={file.url}
              download={file.name}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: 8, marginRight: 10, color: '#1890ff' }}
            >
              <DownloadOutlined style={{ color: 'gray' }} />
            </a>
          )}
        </>
      ),
    });
  };

  const uploadProps = {
    accept: '.pdf',
    itemRender,
    onRemove: (file) => {
      setFileList((curr) => {
        const index = curr.indexOf(file);
        const newFileList = curr.slice();
        newFileList.splice(index, 1);
        return newFileList;
      });
      onFileUploaded(file, 'removed');
    },
    onPreview: (file) => {
      onClickFile(file);
    },
    fileList,
    // onChange: handleFileChange,
    beforeUpload,
    multiple: true,
    ...overrideUploadProps,
  };

  return (
    <div
      className={`document-uploader-container ${
        customClass ? customClass : ''
      }`}
    >
      {showTitle && (
        <div>
          <b>Add Documents:</b>
        </div>
      )}
      <DocumentUploadList
        uploadProps={uploadProps}
        maxVisibleItems={maxVisibleItems}
      />
      <ConfirmScanModal
        isVisible={showScanModal && showConfirmationModal}
        onConfirm={(forScan) => handleConfirmScan(forScan)}
        onCancel={() => handleConfirmScan([])}
        files={pending}
      />
    </div>
  );
};

DocumentUploader.propTypes = {
  onFileUploaded: PropTypes.func,
  wo_files: PropTypes.object,
  overrideUploadProps: PropTypes.object,
  showTitle: PropTypes.bool,
  maxVisibleItems: PropTypes.number,
  customClass: PropTypes.string,
  onClickFile: PropTypes.func,
  forScan: PropTypes.array,
  showScanModal: PropTypes.bool,
};

DocumentUploader.defaultProps = {
  onFileUploaded: () => {},
  wo_files: {
    wo_images: [],
    wo_documents: [],
  },
  overrideUploadProps: {},
  showTitle: true,
  maxVisibleItems: null,
  customClass: '',
  onClickFile: () => {},
  forScan: [],
  showScanModal: true,
};

export default DocumentUploader;
